import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useParams, useNavigate } from "react-router-dom";

export default function ChatRoom({ user }) {
  const { roomId } = useParams();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  const messagesRef = collection(db, `rooms/${roomId}/messages`);
  const q = query(messagesRef, orderBy("timestamp", "asc"));

  useEffect(() => {
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(msgs);
    });

    return unsubscribe;
  }, [roomId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    try {
  const res = await fetch("https://ai-chat-backend-6lf6.onrender.com/analyze", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message }),
  });

  const data = await res.json();
      const sentiment = data.sentiment || "Neutral";

      await addDoc(messagesRef, {
        text: message,
        user: user.email,
        timestamp: serverTimestamp(),
        sentiment,
      });

      setMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
      alert("Failed to send message. Try again.");
    }
  };

  const handleLeaveRoom = async () => {
    try {
      const snapshot = await onSnapshot(messagesRef, () => {});
      snapshot.docs.forEach(async (docItem) => {
        await deleteDoc(doc(messagesRef, docItem.id));
      });
    } catch (error) {
      console.error("Failed to delete messages:", error);
    }

    navigate("/room");
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/chat/${roomId}`;
    navigator.clipboard.writeText(url).then(() => {
      alert("🔗 Room link copied to clipboard!");
    });
  };

  const emojiMap = {
    Positive: "😊",
    Neutral: "😐",
    Negative: "😠",
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #1f1f47, #3f3f89)",
        minHeight: "100vh",
        padding: "30px 20px",
        color: "#fff",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "700px",
          margin: "0 auto",
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: "20px",
          padding: "30px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(15px)",
        }}
      >
        {/* Header with Room ID and Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: "18px" }}>
              Room ID: <span style={{ color: "#00e0d0" }}>{roomId}</span>
            </h2>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleCopyLink}
              style={{
                background: "#00c9a7",
                border: "none",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: "10px",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              📎 Copy Link
            </button>
            <button
              onClick={handleLeaveRoom}
              style={{
                background: "#ff4d4f",
                border: "none",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: "10px",
                fontWeight: "bold",
              }}
            >
              Leave Room
            </button>
          </div>
        </div>

        {/* Message Area */}
        <div
          style={{
            height: "400px",
            overflowY: "auto",
            background: "#252554",
            padding: "20px",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                alignSelf: msg.user === user.email ? "flex-end" : "flex-start",
                background: msg.user === user.email ? "#4f6dff" : "#5c5cff",
                padding: "10px 16px",
                borderRadius: "16px",
                marginBottom: "10px",
                maxWidth: "80%",
                wordBreak: "break-word",
              }}
            >
              <div style={{ fontSize: "14px", opacity: 0.8 }}>{msg.user}</div>
              <div style={{ fontSize: "16px", marginTop: "4px" }}>
                {msg.text}
                <span style={{ marginLeft: "10px" }}>
                  {emojiMap[msg.sentiment] || "🤔"}
                </span>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Input Form */}
        <form
          onSubmit={handleSend}
          style={{ marginTop: "20px", display: "flex", gap: "10px" }}
        >
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "10px",
              border: "none",
              outline: "none",
              fontSize: "16px",
            }}
          />
          <button
            type="submit"
            style={{
              background: "#00c9a7",
              border: "none",
              color: "#fff",
              padding: "12px 20px",
              borderRadius: "10px",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
